/**
 * Netmon site grid at /it/netmon.
 *
 * Card per active site with health badge + transport badge. Search by name or
 * site_id; filter by health (All|Down|Critical|Degraded|Healthy), transport
 * (All|Cellular|Wired|Unknown), and Project Possible (the 16 stores Steven
 * flagged as the strategic Q2 focus). Sort defaults to Down > Critical >
 * Degraded > Healthy then site_id ASC numeric.
 *
 * Health badge has a hover tooltip showing why a site is degraded/critical/down.
 * "Down" (red) means the store's internet is down, from the provider-side check
 * (UniFi cloud where the store has a UniFi router, else Cato, else a ping from
 * the Otto PC for Granite stores); the tooltip
 * names the source and the start time. "Critical" (orange) is heavy packet loss
 * seen by the store PC in the last 15 minutes. "Degraded" (yellow) is light loss,
 * high latency, DNS/TCP failures, or a gateway flap. A store PC that stops
 * reporting is never Down: the card shows a grey "Store PC not reporting" note.
 * When the internet check cannot tell (no check in 15 minutes, or the router
 * blocks ping) the card shows a grey "Internet unknown" note, never red.
 *
 * The Project Possible filter state is persisted in the URL (?project_possible=
 * true) so back-navigation from the SiteDetail page lands on the same view.
 */

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import {
  AlertTriangle,
  Cable,
  HelpCircle,
  Loader2,
  MinusCircle,
  Radio,
  Search,
  Signal,
  SignalLow,
  Sparkles,
  X,
} from 'lucide-react';

import { InfoTooltip } from '@ampersandbrands/ui';
import { Pill } from '@/components/Pill';
import {
  formatLocalTime,
  healthBadgeClass,
  healthDotClass,
  healthSortWeight,
  internetUnknownText,
  pcNotReportingText,
  reasonNotMonitoredLabel,
  relativeTime,
  siteHealthReason,
  useNetmonSites,
  type Health,
  type NetmonSite,
  type ReasonNotMonitored,
  type Transport,
  ispProviderLabel,
  type IspProvider,
} from '@/lib/useNetmon';

// 'unmonitored' is appended after the severity-first health tiers; the store is
// listed but not actively probed, so it is not swept into any health tier.
type HealthFilter = 'all' | 'red' | 'orange' | 'yellow' | 'green' | 'unmonitored';
type TransportFilter = 'all' | Transport;
type ProviderFilter = 'all' | IspProvider;

function ProjectPossibleBadge() {
  return (
    <Pill
      title="Project Possible focus store"
      className="uppercase tracking-caps-pill bg-amp-cream-warm text-amp-olive-deep border-amp-cream-warm dark:bg-amp-olive-dark/20 dark:text-amp-olive-dark dark:border-transparent"
    >
      <Sparkles className="h-3 w-3" />
      Possible
    </Pill>
  );
}

function HealthBadge({ site }: { site: NetmonSite }) {
  const label =
    site.health === 'green'
      ? 'Healthy'
      : site.health === 'yellow'
        ? 'Degraded'
        : site.health === 'orange'
          ? 'Critical'
          : 'Down';
  const reason = siteHealthReason(site);
  // Native title attribute is keyboard-friendly enough for now; swap to a
  // shared Tooltip primitive if the project standardizes one.
  return (
    <Pill tone={healthBadgeClass(site.health)} title={reason || label}>
      <span className={`h-2 w-2 rounded-full ${healthDotClass(site.health)}`} aria-hidden />
      {label}
    </Pill>
  );
}

// Shown in place of the health tier badge for a store that is listed but not
// actively probed. Muted so it reads as informational, never as a Down alert.
function NotMonitoredBadge({ reason }: { reason: ReasonNotMonitored | null }) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <Pill
        tone="bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted"
        title="Store is listed but not actively probed"
      >
        <MinusCircle className="h-2 w-2" aria-hidden />
        Not monitored
      </Pill>
      <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {reasonNotMonitoredLabel(reason)}
      </span>
    </div>
  );
}

// Grey, informational notes under the badges: internet status unknown, and a
// store PC that stopped reporting. Neither changes the health tier.
function StatusNotes({ site }: { site: NetmonSite }) {
  const notes = [internetUnknownText(site), pcNotReportingText(site)].filter(
    (n): n is string => !!n,
  );
  if (notes.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-0.5">
      {notes.map((n) => (
        <p
          key={n}
          className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted inline-flex items-center gap-1"
        >
          <HelpCircle className="h-3 w-3 shrink-0" aria-hidden />
          {n}
        </p>
      ))}
    </div>
  );
}

function ProviderBadge({ provider }: { provider: IspProvider | null | undefined }) {
  return (
    <span
      title="Internet provider"
      className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-xs font-medium dark:bg-slate-800/60 dark:text-slate-300"
    >
      Provider: {ispProviderLabel(provider)}
    </span>
  );
}

function TransportBadge({ transport }: { transport: Transport }) {
  if (transport === 'cellular') {
    return (
      <span
        title="Cellular WAN"
        className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-xs font-medium dark:bg-blue-900/40 dark:text-blue-300"
      >
        <Radio className="h-3 w-3" />
        Cellular
      </span>
    );
  }
  if (transport === 'wired') {
    return (
      <span
        title="Wired WAN"
        className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-medium dark:bg-emerald-900/40 dark:text-emerald-300"
      >
        <Cable className="h-3 w-3" />
        Wired
      </span>
    );
  }
  return (
    <span
      title="Transport not yet classified"
      className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-xs font-medium dark:bg-slate-800/60 dark:text-slate-300"
    >
      <HelpCircle className="h-3 w-3" />
      Unknown
    </span>
  );
}

function SiteCard({ s }: { s: NetmonSite }) {
  // Unmonitored stores carry stale probe-derived values; blank them so loss,
  // latency, and flap counts never read as real telemetry for those rows.
  const lossText =
    !s.monitored || s.worst_loss_pct == null
      ? '--'
      : `${s.worst_loss_pct.toFixed(1)}%`;
  const latencyText =
    !s.monitored || s.worst_latency_ms == null
      ? '--'
      : `${s.worst_latency_ms.toFixed(0)}ms`;
  const flapsText = s.monitored ? String(s.recent_flap_down) : '--';
  const lastSampleAbsolute = formatLocalTime(s.last_sample_at);
  const isProjectPossible = s.project_possible === 1;

  return (
    <Link
      to={`/it/netmon/${encodeURIComponent(s.site_id)}`}
      className={`block border rounded-lg p-4 transition-colors ${
        isProjectPossible
          ? 'border-amp-olive bg-amp-cream hover:border-amp-olive-deep dark:border-amp-olive-dark dark:bg-amp-dark-surface-warm dark:hover:border-amp-olive-dark'
          : 'border-amp-line bg-amp-white hover:border-amp-olive dark:border-amp-dark-line dark:bg-amp-dark-surface dark:hover:border-amp-olive-dark'
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <h3 className="text-[16px] font-semibold text-amp-ink dark:text-amp-dark-ink line-clamp-1">
            #{s.site_id} {s.site_name}
          </h3>
          <div className="flex items-center gap-2 mt-0.5">
            {s.monitored ? (
              <p
                className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted"
                title={`Last sample: ${lastSampleAbsolute}`}
              >
                last sample {relativeTime(s.last_sample_at)}
              </p>
            ) : (
              <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                not actively monitored
              </p>
            )}
            {isProjectPossible && <ProjectPossibleBadge />}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {s.monitored ? (
            <>
              <HealthBadge site={s} />
              <TransportBadge transport={s.transport} />
            </>
          ) : (
            <NotMonitoredBadge reason={s.reason_not_monitored} />
          )}
          <ProviderBadge provider={s.isp_provider} />
        </div>
      </div>
      {s.monitored && <StatusNotes site={s} />}
      <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
        <div>
          <div className="text-amp-ink-muted dark:text-amp-dark-ink-muted">Loss</div>
          <div className="font-medium text-amp-ink dark:text-amp-dark-ink">{lossText}</div>
        </div>
        <div>
          <div className="text-amp-ink-muted dark:text-amp-dark-ink-muted">Latency</div>
          <div className="font-medium text-amp-ink dark:text-amp-dark-ink">{latencyText}</div>
        </div>
        <div>
          <div className="text-amp-ink-muted dark:text-amp-dark-ink-muted">Flaps 15m</div>
          <div className="font-medium text-amp-ink dark:text-amp-dark-ink">
            {flapsText}
          </div>
        </div>
      </div>
    </Link>
  );
}

function compareSiteIds(a: string, b: string): number {
  const an = Number(a);
  const bn = Number(b);
  if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
  return a.localeCompare(b);
}

export function NetmonSites() {
  const sites = useNetmonSites();

  // Filter state lives in URL search params so back-navigation from the
  // SiteDetail page restores the same view. Keys: q, health, transport, provider,
  // project_possible.
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('q') ?? '';
  const healthFilter = (searchParams.get('health') ?? 'all') as HealthFilter;
  const transportFilter = (searchParams.get('transport') ?? 'all') as TransportFilter;
  const providerFilter = (searchParams.get('provider') ?? 'all') as ProviderFilter;
  const projectPossibleOnly = searchParams.get('project_possible') === 'true';

  const updateParam = (key: string, value: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null || value === '' || value === 'all' || value === 'false') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  };

  // Banner counts use the full dataset (pre-filter) so the "stores need
  // attention" total stays honest regardless of which view the user has
  // chosen. Filters affect only the rendered grid below.
  // Attention banner counts only monitored stores; an unmonitored store's
  // health is stale and must not inflate the down/critical/degraded totals.
  const downCount = useMemo(
    () => sites.sites.filter((s) => s.monitored && s.health === 'red').length,
    [sites.sites],
  );
  const criticalCount = useMemo(
    () => sites.sites.filter((s) => s.monitored && s.health === 'orange').length,
    [sites.sites],
  );
  const degradedCount = useMemo(
    () => sites.sites.filter((s) => s.monitored && s.health === 'yellow').length,
    [sites.sites],
  );
  const projectPossibleCount = useMemo(
    () => sites.sites.filter((s) => s.project_possible === 1).length,
    [sites.sites],
  );

  const visibleSites = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = sites.sites.filter((s) => {
      if (projectPossibleOnly && s.project_possible !== 1) return false;
      if (healthFilter === 'unmonitored') {
        if (s.monitored) return false;
      } else if (healthFilter !== 'all') {
        // Health tiers apply to monitored stores only; unmonitored stores carry
        // a stale health value and must not surface under Down/Critical/etc.
        if (!s.monitored) return false;
        if (s.health !== (healthFilter as Health)) return false;
      }
      if (transportFilter !== 'all' && s.transport !== transportFilter) return false;
      if (providerFilter !== 'all' && s.isp_provider !== providerFilter) return false;
      if (q) {
        const hay =
          `${s.site_id} ${s.site_name}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // Sort: Down > Critical > Degraded > Healthy, then site_id ASC numeric.
    filtered.sort((a, b) => {
      const wDiff = healthSortWeight(b.health) - healthSortWeight(a.health);
      if (wDiff !== 0) return wDiff;
      return compareSiteIds(a.site_id, b.site_id);
    });
    return filtered;
  }, [sites.sites, search, healthFilter, transportFilter, providerFilter, projectPossibleOnly]);

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
            IT
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">
            Network monitor
          </h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Per-store latency, loss, DNS and LAN-device telemetry from the BOH probes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ToolPinToggle toolSlug="netmon" toolName="Network monitor" />
          <Link
            to="/it/netmon/snipeit"
            className="text-xs border border-amp-line text-amp-ink rounded px-3 py-2 hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            Snipe-IT reconcile
          </Link>
        </div>
      </div>

      {downCount + criticalCount + degradedCount > 0 && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-900 dark:text-amber-200 inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          Stores need attention:{' '}
          {[
            downCount > 0 ? `${downCount} down` : null,
            criticalCount > 0 ? `${criticalCount} critical` : null,
            degradedCount > 0 ? `${degradedCount} degraded` : null,
          ]
            .filter(Boolean)
            .join(', ')}
          .
        </div>
      )}

      {/* Project Possible filter pill. The 16 stores that are Steven's
          strategic Q2 focus — prominent placement above the rest of the
          filter row so it's a one-click flip. */}
      <div className="mb-3 inline-flex items-center gap-2">
        <button
          type="button"
          onClick={() =>
            updateParam('project_possible', projectPossibleOnly ? null : 'true')
          }
          aria-pressed={projectPossibleOnly}
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium border transition-colors ${
            projectPossibleOnly
              ? 'bg-amp-olive text-amp-white border-amp-olive-deep hover:bg-amp-olive-deep dark:bg-amp-olive-dark dark:text-amp-dark-bg dark:border-amp-olive-dark dark:hover:bg-amp-olive'
              : 'bg-amp-white text-amp-ink border-amp-line hover:border-amp-olive hover:bg-amp-cream-warm dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:border-amp-dark-line dark:hover:border-amp-olive-dark'
          }`}
        >
          <Sparkles className="h-4 w-4" />
          Project Possible
          <span
            className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
              projectPossibleOnly
                ? 'bg-amp-white/20 text-amp-white dark:bg-amp-dark-bg/30 dark:text-amp-dark-bg'
                : 'bg-amp-cream-warm text-amp-olive-deep dark:bg-amp-olive-dark/20 dark:text-amp-olive-dark'
            }`}
          >
            {projectPossibleCount} of {sites.sites.length}
          </span>
        </button>
        <InfoTooltip label="Project Possible">
          The 16 Lolli &amp; Pops stores in Project Possible. Steven&apos;s strategic
          focus.
        </InfoTooltip>
      </div>

      {/* Search + filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-amp-ink-muted dark:text-amp-dark-ink-muted pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => updateParam('q', e.target.value)}
            placeholder="Search by store name or number"
            className="pl-8 pr-8 py-1.5 text-sm border border-amp-line rounded bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink w-64"
          />
          {search && (
            <button
              type="button"
              onClick={() => updateParam('q', '')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <label className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted inline-flex items-center gap-2">
          Health
          <select
            value={healthFilter}
            onChange={(e) => updateParam('health', e.target.value)}
            className="text-sm border border-amp-line rounded px-2 py-1 bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          >
            <option value="all">All</option>
            <option value="red">Down</option>
            <option value="orange">Critical</option>
            <option value="yellow">Degraded</option>
            <option value="green">Healthy</option>
            <option value="unmonitored">Not monitored</option>
          </select>
        </label>
        <label className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted inline-flex items-center gap-2">
          Transport
          <select
            value={transportFilter}
            onChange={(e) => updateParam('transport', e.target.value)}
            className="text-sm border border-amp-line rounded px-2 py-1 bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          >
            <option value="all">All</option>
            <option value="cellular">Cellular</option>
            <option value="wired">Wired</option>
            <option value="unknown">Unknown</option>
          </select>
        </label>
        <label className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted inline-flex items-center gap-2">
          Provider
          <select
            value={providerFilter}
            onChange={(e) => updateParam('provider', e.target.value)}
            className="text-sm border border-amp-line rounded px-2 py-1 bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          >
            <option value="all">All</option>
            <option value="uniti">Uniti</option>
            <option value="granite">Granite</option>
            <option value="other">Other</option>
          </select>
        </label>
        {(search ||
          providerFilter !== 'all' ||
          healthFilter !== 'all' ||
          transportFilter !== 'all' ||
          projectPossibleOnly) && (
          <button
            type="button"
            onClick={() => {
              setSearchParams(new URLSearchParams(), { replace: true });
            }}
            className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            Reset filters
          </button>
        )}
        <div className="ml-auto text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Showing {visibleSites.length} of {sites.sites.length}
        </div>
      </div>

      {sites.isLoading ? (
        <div className="border border-amp-line rounded-lg bg-amp-white p-6 dark:border-amp-dark-line dark:bg-amp-dark-surface inline-flex items-center gap-2 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading sites...
        </div>
      ) : sites.error ? (
        <div className="border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/30 rounded-lg p-4 text-sm text-red-900 dark:text-red-200">
          Could not load sites: {sites.error.message}
        </div>
      ) : sites.sites.length === 0 ? (
        <div className="border border-amp-line rounded-lg bg-amp-white p-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
          <h2 className="font-medium text-amp-ink dark:text-amp-dark-ink mb-2 inline-flex items-center gap-2">
            <SignalLow className="h-4 w-4" />
            No active sites yet.
          </h2>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Sites register themselves on first ingest. Confirm the Atera deployment
            has rolled out (Tasks 5-6).
          </p>
        </div>
      ) : visibleSites.length === 0 ? (
        <div className="border border-amp-line rounded-lg bg-amp-white p-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            No sites match the current filters.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleSites.map((s) => (
            <SiteCard key={s.site_id} s={s} />
          ))}
        </div>
      )}

      <div className="mt-6 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted inline-flex items-center gap-2">
        <Signal className="h-3 w-3" /> Auto-refresh every 30s.
      </div>
    </div>
  );
}
